import { z } from "zod";

// ── List Status ──

// Workflow order: the front derives its option list from it, so the order
// here IS the order an admin sees. `draft` is not one of the ticket's six
// stages (#517) but it is what a list is created as, and rows carry it.
export const ListStatusSchema = z.enum([
    "draft",
    "planned",
    "in_progress",
    "pending_review",
    "email_enrichment",
    "completed",
    "sent",
]);
export type ListStatus = z.infer<typeof ListStatusSchema>;

/**
 * The stages an approval can be asked from (#577): before the review. Asking
 * moves the list to `pending_review`, so from there on there is nothing left
 * to ask; the reviewer answers by moving the list on (or back).
 */
export const APPROVAL_REQUESTABLE_STATUSES = ["draft", "planned", "in_progress"] as const satisfies readonly ListStatus[];

export function canRequestApproval(status: ListStatus): boolean {
    return (APPROVAL_REQUESTABLE_STATUSES as readonly ListStatus[]).includes(status);
}

/**
 * The stages at which a list counts as delivered (#716): its content is the
 * one the client received, so reactivating a profile never puts it back there.
 */
export const DELIVERED_LIST_STATUSES = ["completed", "sent"] as const satisfies readonly ListStatus[];

// ── Client-facing scope description (#718) ──

export const CLIENT_SCOPE_DESCRIPTION_REQUIRED =
    "Please complete the client-facing scope description before sending this list.";

/**
 * Whether a list would stand `sent` without a client-facing description (#718).
 * Judged on the list as it will be saved, not on the change: reaching `sent`
 * with a blank description, emptying it on a sent list and re-saving a list
 * sent before the field existed all fall under it. The server refuses the
 * save; Edit list blocks it first, with the same message.
 *
 * A client's own import (`origin` client) is exempt (#722): nobody at Invyo
 * wrote it a brief, so there is nothing to describe to the client.
 */
export function isClientScopeDescriptionMissing(
    status: ListStatus | string,
    description: string,
    origin: ListOrigin | string,
): boolean {
    return origin !== "client" && status === "sent" && description.trim() === "";
}

// ── Import Status ──

/**
 * A client import's outcome (#708), with the enrichment sessions' labels:
 * every row imported, some, or none. Null on a list no client import wrote
 * (imports made before 2.8.0, admin lists, the extension's additions).
 */
export const ImportStatusSchema = z.enum(["COMPLETED", "PARTIALLY_FAILED", "FAILED"]);
export type ImportStatus = z.infer<typeof ImportStatusSchema>;

// ── List Response ──

export const ListOriginSchema = z.enum(["client", "invyo"]);
export type ListOrigin = z.infer<typeof ListOriginSchema>;

export const ListSourceSchema = z.enum(["extension", "csv_import", "api", "manual", "crm_sync"]);
export type ListSource = z.infer<typeof ListSourceSchema>;

export const ListDataProviderSchema = z.enum(["linkedin", "datagouv", "pappers", "crunchbase", "dealroom", "manual", "unknown"]);
export type ListDataProvider = z.infer<typeof ListDataProviderSchema>;

export const ListResponseSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    licence_id: z.uuid(),
    licence_name: z.string().nullable().optional(),
    start_date: z.string(),
    end_date: z.string(),
    /**
     * The internal brief, also the AI search query. Admins only: the API leaves
     * it out of every answer to a client (#718), who reads
     * `client_scope_description` instead.
     */
    scope: z.string().optional(),
    /**
     * What the client reads about the list (#718), written for them by an Invyo
     * expert. Empty on lists sent before it existed; required to be sent.
     */
    client_scope_description: z.string(),
    status: ListStatusSchema,
    origin: ListOriginSchema,
    source: ListSourceSchema,
    data_provider: ListDataProviderSchema,
    profile_count: z.number().int(),
    company_count: z.number().int(),
    /**
     * When the list last reached `sent` (#560). Null for a list never sent. Lists
     * sent before the date was recorded carry their licence's opening date (#712).
     */
    sent_at: z.string().nullable(),
    /**
     * Who imported the list, as "First Last" (#687). Only `GET /lists?origin=client`
     * sets it, from the `list_created` activity event; null when no event names
     * the user (lists older than 2.2.0, deleted user). Absent everywhere else.
     */
    imported_by: z.string().nullable().optional(),
    /**
     * A client import's outcome (#708): its status and the file's rows, those
     * imported and those in error (rejected at Analyze included). All null on
     * a list no client import wrote; there is no back-fill.
     */
    import_status: ImportStatusSchema.nullable().optional(),
    import_rows_total: z.number().int().nullable().optional(),
    import_rows_imported: z.number().int().nullable().optional(),
    import_rows_failed: z.number().int().nullable().optional(),
    /**
     * Where the list's data came from (#749): set by a client file import and
     * by a client's list created from the extension (`invyo_extension`),
     * copied by Duplicate. All null on any other list; there is no back-fill.
     */
    data_source_category: z.string().nullable().optional(),
    data_source_tool: z.string().nullable().optional(),
    data_source_detail: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
});
export type ListResponse = z.infer<typeof ListResponseSchema>;

// ── Create List ──

export const CreateListRequestSchema = z.object({
    name: z.string().min(1),
    start_date: z.string(),
    end_date: z.string(),
    scope: z.string().optional().default(""),
    /** #718: required (trimmed) when the list is created straight into `sent`. */
    client_scope_description: z.string().optional().default(""),
    status: ListStatusSchema.optional().default("draft"),
    origin: ListOriginSchema.optional().default("invyo"),
    source: ListSourceSchema.optional().default("manual"),
    data_provider: ListDataProviderSchema.optional().default("unknown"),
    /** Admin-only: override the licence this list is created for. */
    licence_id: z.uuid().optional(),
});
export type CreateListRequest = z.infer<typeof CreateListRequestSchema>;

// ── Update List ──

export const UpdateListRequestSchema = z.object({
    name: z.string().min(1).optional(),
    start_date: z.string().optional(),
    end_date: z.string().optional(),
    scope: z.string().optional(),
    /** #718: cannot be left blank (trimmed) on a list whose final status is `sent`. */
    client_scope_description: z.string().optional(),
    status: ListStatusSchema.optional(),
    /**
     * Admin-only (#520): hand the list to another licence. Its users gain
     * full access, the previous licence's users lose it. 403 for anyone else.
     */
    licence_id: z.uuid().optional(),
});
export type UpdateListRequest = z.infer<typeof UpdateListRequestSchema>;

// ── Duplicate List ──

/**
 * Copy a list, memberships included.
 *
 * The name is required and comes from the caller rather than being derived here:
 * the naming convention is a product rule the interface teaches and the user can
 * override in the dialog, and a copy that lands under a name nobody chose is a
 * copy nobody finds. The licence is not a field: a copy stays where its
 * original was, and "Change licence" moves it afterwards.
 */
export const DuplicateListRequestSchema = z.object({
    name: z.string().min(1),
});
export type DuplicateListRequest = z.infer<typeof DuplicateListRequestSchema>;

// ── Bulk delete (#577) ──

export const BULK_DELETE_LISTS_MAX_IDS = 500;

export const BulkDeleteListsRequestSchema = z.object({
    list_ids: z.array(z.uuid()).min(1).max(BULK_DELETE_LISTS_MAX_IDS),
});
export type BulkDeleteListsRequest = z.infer<typeof BulkDeleteListsRequestSchema>;

export const BulkDeleteListsResponseSchema = z.object({
    deleted_count: z.number().int(),
});
export type BulkDeleteListsResponse = z.infer<typeof BulkDeleteListsResponseSchema>;

// ── Request approval (#577) ──

/** Who is asked to approve: an Invyo staff account, picked at each request. */
export const RequestListApprovalRequestSchema = z.object({
    administrator_id: z.uuid(),
});
export type RequestListApprovalRequest = z.infer<typeof RequestListApprovalRequestSchema>;

// ── Deduplicate (#640) ──

/**
 * Which other lists of the licence a list is deduplicated against: only those
 * already sent to the client, or every other one (drafts and client-made lists
 * included).
 */
export const DedupeScopeSchema = z.enum(["sent", "all"]);
export type DedupeScope = z.infer<typeof DedupeScopeSchema>;

export const DeduplicateListRequestSchema = z.object({
    scope: DedupeScopeSchema,
});
export type DeduplicateListRequest = z.infer<typeof DeduplicateListRequestSchema>;

/**
 * Preview of a deduplication. `duplicate_count` counts each profile once; a
 * profile also present in two other lists shows up in both `lists` entries.
 */
export const ListDuplicatesResponseSchema = z.object({
    scope: DedupeScopeSchema,
    duplicate_count: z.number().int(),
    lists: z.array(z.object({
        id: z.uuid(),
        name: z.string(),
        status: ListStatusSchema,
        count: z.number().int(),
    })),
});
export type ListDuplicatesResponse = z.infer<typeof ListDuplicatesResponseSchema>;

export const DeduplicateListResponseSchema = z.object({
    list_id: z.uuid(),
    profile_count: z.number().int(),
    removed_count: z.number().int(),
});
export type DeduplicateListResponse = z.infer<typeof DeduplicateListResponseSchema>;

/**
 * `GET /lists/import-summary` (#695): what the client Imports page cannot count
 * from its rows.
 *  - `distinct_profiles`: the profiles across every client import of the licence,
 *    each counted once, deactivated ones included (a list's `profile_count` holds
 *    its active members only, #620);
 *  - `csm`: the licence's Customer Success Manager, by name only (never their
 *    email), whom the "Contact My Customer Success Manager" form writes to; null
 *    when the licence has none, and the button is then hidden.
 */
export const ImportSummarySchema = z.object({
    distinct_profiles: z.number().int(),
    csm: z.object({
        first_name: z.string(),
        last_name: z.string(),
    }).nullable(),
});
export type ImportSummary = z.infer<typeof ImportSummarySchema>;

/**
 * A row of the client Discovered LPs table (#712): a delivered (Sent) Invyo list,
 * with two figures the other list tables do not need.
 *
 * - `lp_types`: the distinct types of the list's active profiles, alphabetical;
 *   profiles with no type add nothing. The page shows the categories these
 *   types belong to, and leaves out the types no category claims.
 * - `profile_company_count`: the distinct companies those profiles currently work
 *   at (current experiences). Unlike `company_count`, which counts the companies
 *   an import attached to the list.
 */
export const DeliveredListResponseSchema = ListResponseSchema.extend({
    lp_types: z.array(z.string()),
    profile_company_count: z.number().int(),
});
export type DeliveredListResponse = z.infer<typeof DeliveredListResponseSchema>;

// ── Wrappers ──

export const ListListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(ListResponseSchema),
});
export type ListListResponse = z.infer<typeof ListListResponseSchema>;

export const DeliveredListListResponseSchema = z.object({
    success: z.boolean(),
    data: z.array(DeliveredListResponseSchema),
});
export type DeliveredListListResponse = z.infer<typeof DeliveredListListResponseSchema>;

export const ListDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ListResponseSchema,
});
export type ListDetailResponse = z.infer<typeof ListDetailResponseSchema>;

export const ImportSummaryDetailResponseSchema = z.object({
    success: z.boolean(),
    data: ImportSummarySchema,
});
export type ImportSummaryDetailResponse = z.infer<typeof ImportSummaryDetailResponseSchema>;
